import React from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import DownloadIcon from '@material-ui/icons/GetApp';
import DQLAB_logo from '../../../assets/images/DQLAB_logo.png';
import { LOCAL_STORAGE_KEYS } from '../../../utils/constants';
import { isDqtInvoice, dqtInvoiceRows, dqtPaymentNote, dqtSectionTitle, isDirectInvoice, directInvoiceRows, directPaymentNote, invoiceCharges } from './dqtInvoiceRows';

const InvoiceDetailsDialog = ({
    open,
    onClose,
    selectedInvoice,
    dqInsiderInfo,
    programNameMap,
    formatDate,
    formatAmount,
    getStatusChipColor,
    handleDownloadInvoice,
    isEducator = false,
}) => {
    const isDqt = isDqtInvoice(selectedInvoice);
    const isDirect = isDirectInvoice(selectedInvoice);
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', backgroundImage: 'none' } }}
        >
            <DialogTitle sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                borderBottom: '2px solid #FF6900', pb: 2
            }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'var(--brand-text, #000)' }}>
                    Invoice Details - {selectedInvoice?.month}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 2 }}>
                {selectedInvoice && (
                    <Box>
                        {/* Company Header with Logo */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2.5, borderBottom: '2px solid var(--brand-border, #e0e0e0)' }}>
                            <Box
                                component="img"
                                src={DQLAB_logo}
                                alt="DQ LAB Logo"
                                sx={{ width: 'auto', height: 50, maxWidth: 120, objectFit: 'contain', mr: 3 }}
                            />
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5, color: 'var(--brand-text, #000)' }}>
                                    DQ LAB Pte. Ltd
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.5 }}>
                                    20A Tanjong Pagar Road, Singapore 088443
                                </Typography>
                            </Box>
                        </Box>

                        {/* Invoice Header Info */}
                        <Box sx={{ mb: 3, p: 2, backgroundColor: 'var(--brand-bg, #f9f9f9)', borderRadius: '8px' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
                                {/* Invoice Number */}
                                <Box sx={{ flex: 1, minWidth: '200px' }}>
                                    <Typography variant="body2" color="textSecondary">Invoice Number</Typography>
                                    <Typography variant="body2" sx={{ color: '#FF6900', fontWeight: 'bold' }}>
                                        {selectedInvoice.invoice_number}
                                    </Typography>
                                </Box>

                                {/* Bill To */}
                                <Box sx={{ flex: 1, minWidth: '200px' }}>
                                    <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold', mb: 0.5 }}>Bill To</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {dqInsiderInfo?.profile?.profile_type === 'BUSINESS'
                                            ? dqInsiderInfo.profile.business_name || 'Business Name Not Available'
                                            : dqInsiderInfo?.profile?.first_name && dqInsiderInfo?.profile?.last_name
                                                ? `${dqInsiderInfo.profile.first_name} ${dqInsiderInfo.profile.last_name}`
                                                : dqInsiderInfo?.user?.email || localStorage.getItem(LOCAL_STORAGE_KEYS.user_email) || 'DQ Insider'}
                                    </Typography>
                                    {dqInsiderInfo?.user?.email && (
                                        <Typography variant="body2" color="textSecondary">{dqInsiderInfo.user.email}</Typography>
                                    )}
                                    {dqInsiderInfo?.profile?.address && (
                                        <Typography variant="body2" color="textSecondary">{dqInsiderInfo.profile.address}</Typography>
                                    )}
                                    {(dqInsiderInfo?.profile?.city || dqInsiderInfo?.profile?.postal_code) && (
                                        <Typography variant="body2" color="textSecondary">
                                            {[dqInsiderInfo.profile.city, dqInsiderInfo.profile.postal_code].filter(Boolean).join(', ')}
                                        </Typography>
                                    )}
                                    {dqInsiderInfo?.profile?.country && (
                                        <Typography variant="body2" color="textSecondary">{dqInsiderInfo.profile.country}</Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>


                        {/* Invoice Summary Table */}
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Invoice Summary</Typography>
                        <TableContainer sx={{ mb: 3, border: '1px solid var(--brand-border, #e0e0e0)', borderRadius: '8px' }}>
                            <Table size="small">
                                <TableHead sx={{ backgroundColor: 'var(--brand-bg, #f5f5f5)' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Total Invoice Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formatDate(selectedInvoice.date)}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={selectedInvoice.status}
                                                size="small"
                                                sx={{ ...getStatusChipColor(selectedInvoice.status), fontWeight: 800, fontSize: '0.65rem', textTransform: 'uppercase', height: '22px' }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#FF6900' }}>
                                                {selectedInvoice.currency} ${formatAmount(selectedInvoice.total_amount)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Charges — each on its own row: what is paid once, and what yearly */}
                        {invoiceCharges(selectedInvoice).length > 0 && (
                            <>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Charges</Typography>
                                <TableContainer sx={{ mb: 3, border: '1px solid var(--brand-border, #e0e0e0)', borderRadius: '8px' }}>
                                    <Table size="small">
                                        <TableHead sx={{ backgroundColor: 'var(--brand-bg, #f5f5f5)' }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Charge</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {invoiceCharges(selectedInvoice).map((c) => (
                                                <TableRow key={c.key}>
                                                    <TableCell><Typography variant="body2">{c.description}</Typography></TableCell>
                                                    <TableCell align="center"><Typography variant="body2" color="textSecondary">{c.type}</Typography></TableCell>
                                                    <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 'bold' }}>{c.amount}</Typography></TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow>
                                                <TableCell colSpan={2}><Typography variant="body2" sx={{ fontWeight: 'bold' }}>Total</Typography></TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#FF6900' }}>
                                                        {selectedInvoice.currency} ${formatAmount(selectedInvoice.total_amount)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}

                        {/* A direct workspace's purchase — licences and credits, never DQ Tokens */}
                        {isDirect && (
                            <>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>What you received</Typography>
                                <TableContainer sx={{ mb: 1, border: '1px solid var(--brand-border, #e0e0e0)', borderRadius: '8px' }}>
                                    <Table size="small">
                                        <TableBody>
                                            {directInvoiceRows(selectedInvoice.units).map(([label, value]) => (
                                                <TableRow key={label}>
                                                    <TableCell sx={{ width: '40%' }}>
                                                        <Typography variant="body2" color="textSecondary">{label}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{value}</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Typography variant="caption" color="textSecondary" component="p" sx={{ mb: 3 }}>
                                    {directPaymentNote(selectedInvoice.units)}
                                </Typography>
                            </>
                        )}

                        {/* DQ Tokens — what was paid and received */}
                        {isDqt && (
                            <>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>{dqtSectionTitle(selectedInvoice.dqt)}</Typography>
                                <TableContainer sx={{ mb: 1, border: '1px solid var(--brand-border, #e0e0e0)', borderRadius: '8px' }}>
                                    <Table size="small">
                                        <TableBody>
                                            {dqtInvoiceRows(selectedInvoice.dqt).map(([label, value]) => (
                                                <TableRow key={label}>
                                                    <TableCell sx={{ width: '40%' }}>
                                                        <Typography variant="body2" color="textSecondary">{label}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{value}</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Typography variant="caption" color="textSecondary" component="p" sx={{ mb: 3 }}>
                                    {dqtPaymentNote(selectedInvoice.dqt)}
                                    {selectedInvoice.hosted_invoice_url && (
                                        <>
                                            {' '}
                                            <a href={selectedInvoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer" style={{ color: '#FF6900' }}>
                                                View the Stripe invoice
                                            </a>
                                        </>
                                    )}
                                </Typography>
                            </>
                        )}

                        {/* Programs Created This Month */}
                        {selectedInvoice.programs_created && selectedInvoice.programs_created.length > 0 && (
                            <>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Programs Created This Month</Typography>
                                <TableContainer sx={{ mb: 3, border: '1px solid var(--brand-border, #e0e0e0)', borderRadius: '8px' }}>
                                    <Table size="small">
                                        <TableHead sx={{ backgroundColor: 'var(--brand-bg, #f5f5f5)' }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Program Name</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Program Code</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Sessions</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Cost (USD)</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Created At</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedInvoice.programs_created.map((program, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{program.program_name}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="textSecondary">{program.program_code}</Typography>
                                                    </TableCell>
                                                    <TableCell align="center">{program.no_of_sessions}</TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#FF6900' }}>
                                                            ${formatAmount(program.cost)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">{formatDate(program.created_at)}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow sx={{ backgroundColor: 'var(--brand-bg, #f9f9f9)' }}>
                                                <TableCell colSpan={3} sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                                                    Total Program Creation Cost:
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#FF6900', fontSize: '1.1rem' }}>
                                                    ${formatAmount(selectedInvoice.programs_created.reduce((sum, p) => sum + (p.cost || 0), 0))}
                                                </TableCell>
                                                <TableCell colSpan={1} />
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}

                        {/* Assessments Created This Month */}
                        {selectedInvoice.assessments_created && selectedInvoice.assessments_created.length > 0 && (
                            <>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Assessments Created This Month</Typography>
                                <TableContainer sx={{ mb: 3, border: '1px solid var(--brand-border, #e0e0e0)', borderRadius: '8px' }}>
                                    <Table size="small">
                                        <TableHead sx={{ backgroundColor: 'var(--brand-bg, #f5f5f5)' }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Assessment Name</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Assessment Code</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Cost (USD)</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Created At</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedInvoice.assessments_created.map((assessment, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{assessment.assessment_name}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="textSecondary">{assessment.assessment_code}</Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#FF6900' }}>
                                                            ${formatAmount(assessment.cost)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">{formatDate(assessment.created_at)}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow sx={{ backgroundColor: 'var(--brand-bg, #f9f9f9)' }}>
                                                <TableCell colSpan={2} sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                                                    Total Assessment Creation Cost:
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#FF6900', fontSize: '1.1rem' }}>
                                                    ${formatAmount(selectedInvoice.assessments_created.reduce((sum, a) => sum + (a.cost || 0), 0))}
                                                </TableCell>
                                                <TableCell />
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}

                        {/* Organization Breakdown — owner only */}
                        {!isEducator && selectedInvoice.organizations && selectedInvoice.organizations.length > 0 && (
                            <>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Organization Breakdown</Typography>
                                <TableContainer sx={{ mb: 3, border: '1px solid var(--brand-border, #e0e0e0)', borderRadius: '8px' }}>
                                    <Table size="small">
                                        <TableHead sx={{ backgroundColor: 'var(--brand-bg, #f5f5f5)' }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Organization</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Licenses</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Users</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedInvoice.organizations.map((org, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{org.name}</Typography>
                                                    </TableCell>
                                                    <TableCell align="center">{org.dqp}</TableCell>
                                                    <TableCell align="center">{org.users}</TableCell>
                                                    <TableCell align="center">${formatAmount(org.amount)}</TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={org.status}
                                                            size="small"
                                                            sx={{ ...getStatusChipColor(org.status), fontSize: '11px', fontWeight: 'bold' }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow sx={{ backgroundColor: 'var(--brand-bg, #f9f9f9)' }}>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                    {selectedInvoice.organizations.reduce((sum, org) => sum + (org.dqp || 0), 0)}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                    {selectedInvoice.organizations.reduce((sum, org) => sum + (org.users || 0), 0)}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#FF6900', fontSize: '1.1rem' }}>
                                                    ${formatAmount(selectedInvoice.organizations.reduce((sum, org) => sum + (org.amount || 0), 0))}
                                                </TableCell>
                                                <TableCell />
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}

                        {/* Program Breakdown */}
                        {selectedInvoice.organizations && selectedInvoice.organizations.length > 0 && (
                            <>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Program Breakdown</Typography>
                                {selectedInvoice.organizations.map((org, orgIndex) => (
                                    <Box key={orgIndex} sx={{ mb: 3 }}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{ mb: 1, fontWeight: 'bold', p: 1, backgroundColor: 'var(--brand-bg, #f9f9f9)', borderLeft: '3px solid #FF6900' }}
                                        >
                                            {org.name} ({org.org_code})
                                        </Typography>

                                        {org.programs?.map((program, progIndex) => (
                                            <Box
                                                key={progIndex}
                                                sx={{
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                    p: 1.5, borderBottom: '1px solid var(--brand-border, #f0f0f0)',
                                                }}
                                            >
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        {programNameMap[program.code] || program.code}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">({program.code})</Typography>
                                                    <Typography variant="caption" color="textSecondary" display="block">
                                                        {program.targetAudience}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 3 }}>
                                                    {[
                                                        { value: program.dqp, label: 'Licenses' },
                                                        { value: program.users, label: 'Users' },
                                                        { value: `$${formatAmount(program.amount)}`, label: 'Amount' },
                                                    ].map(({ value, label }) => (
                                                        <Box key={label} sx={{ textAlign: 'center' }}>
                                                            <Typography variant="h6" sx={{ color: '#FF6900', fontWeight: 'bold' }}>{value}</Typography>
                                                            <Typography variant="caption" color="textSecondary">{label}</Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        ))}

                                        {(!org.programs || org.programs.length === 0) && (
                                            <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                                                No programs found
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={onClose}
                    sx={{
                        color: '#FF6900', borderColor: '#FF6900', px: 2.5, py: 0.75, fontSize: '0.875rem',
                        '&:hover': { borderColor: '#e55458', backgroundColor: 'rgba(255, 100, 104, 0.05)' }
                    }}
                >
                    Close
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => selectedInvoice && handleDownloadInvoice(selectedInvoice)}
                    sx={{
                        backgroundColor: '#FF6900', color: '#ffffff', fontWeight: 'bold',
                        px: 2.5, py: 0.75, fontSize: '0.875rem', '&:hover': { backgroundColor: '#e55458' }
                    }}
                >
                    Download PDF
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InvoiceDetailsDialog;
